// react
import { useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link, useNavigate } from '@tanstack/react-router';

// material ui icons
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import LightRoundedIcon from '@mui/icons-material/LightRounded';

// material ui
import { Box, Chip, IconButton, Stack, Typography } from '@mui/material';
import {
  DataGridPremium,
  GridColDef,
  GridRenderCellParams,
} from '@mui/x-data-grid-premium';

// api
import type {
  GetDeviceDto,
  GetDeviceUnitDto,
} from '@/api/generated/recalc/endpoints/v2/devices';

// shared components
import { DataGridToolbar } from '@/components/ui/datagrid/DataGridToolbar';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';
import { showSnackbar } from '@/redux/snackbarSlice';

// relative
import {
  formatCapabilityLabel,
  formatDeviceLocation,
  formatDeviceTag,
  formatDeviceTypeLabel,
} from './devices.utils';
import { deriveDeviceStatus } from './deviceStatus.utils';
import DeviceStatusCell from './DeviceStatusCell';
import DeviceTagChip from './DeviceTagChip';

interface DeviceDataGridProps {
  accountId: number;
  devices?: GetDeviceDto[];
  isLoading?: boolean;
}

type DeviceRow = {
  id: number | string;
  deviceId?: number;
  status: string;
  displayName: string;
  deviceType: string;
  description: string;
  location: string;
  mappedUnits: GetDeviceUnitDto[];
  tags: GetDeviceDto['tags'];
  capabilities: GetDeviceDto['capabilities'];
  raw: GetDeviceDto;
};

type DeviceTag = NonNullable<NonNullable<GetDeviceDto['tags']>>[number];

const DeviceGridToolbar = () => (
  <DataGridToolbar showExport showFilters showColumns />
);

const DeviceDataGrid = ({
  accountId,
  devices = [],
  isLoading = false,
}: DeviceDataGridProps) => {
  const { t } = useTranslation('units-gateways-sensors');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const localeText = useDatagridLocaleText();

  const rows = useMemo<DeviceRow[]>(
    () =>
      devices.map((device, index) => ({
        id: device.deviceId ?? `${accountId}-${index}`,
        deviceId: device.deviceId,
        status: deriveDeviceStatus(device),
        displayName: device.displayName?.trim() || '',
        deviceType: device.deviceType?.trim() || '',
        description: device.description?.trim() || '',
        location: formatDeviceLocation(device.location),
        mappedUnits: device.mappedUnits ?? [],
        tags: device.tags ?? [],
        capabilities: device.capabilities ?? {},
        raw: device,
      })),
    [accountId, devices],
  );

  const availableTypes = useMemo(
    () =>
      Array.from(
        new Set(rows.map((row) => row.deviceType).filter(Boolean)),
      ).sort((left, right) =>
        formatDeviceTypeLabel(left).localeCompare(formatDeviceTypeLabel(right)),
      ),
    [rows],
  );

  const [selectedType, setSelectedType] = useState<string>('all');

  const filteredRows = useMemo(
    () =>
      selectedType === 'all'
        ? rows
        : rows.filter((row) => row.deviceType === selectedType),
    [rows, selectedType],
  );

  const handleCopyId = async (device: GetDeviceDto) => {
    if (!device.deviceId) {
      return;
    }

    try {
      await navigator.clipboard.writeText(String(device.deviceId));
      dispatch(
        showSnackbar({
          message: t('devices.deviceIdCopied', { id: device.deviceId }),
          severity: 'success',
        }),
      );
    } catch (error) {
      console.error('Copy device id failed:', error);
      dispatch(
        showSnackbar({
          message: t('devices.deviceIdCopyFailed'),
          severity: 'error',
        }),
      );
    }
  };

  const handleRowClick = (row: DeviceRow) => {
    if (!row.deviceId) {
      return;
    }

    navigate({ to: `/$accountId/devices/${row.deviceId}` });
  };

  const columns: GridColDef<DeviceRow>[] = [
    {
      field: 'status',
      headerName: '',
      description: t('devices.status', { defaultValue: 'Status' }),
      width: 72,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => null,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height="100%"
        >
          <DeviceStatusCell device={params.row.raw} hideLabel />
        </Box>
      ),
      valueFormatter: (value: string) =>
        ({
          online: t('devices.statusOnline', { defaultValue: 'Online' }),
          stale: t('devices.statusStale', { defaultValue: 'Stale' }),
          offline: t('devices.statusOffline', {
            defaultValue: 'Never connected',
          }),
        })[value] ?? value,
    },
    {
      field: 'displayName',
      headerName: t('devices.displayName'),
      flex: 1.2,
      valueFormatter: (value) => value || t('common.notAvailable'),
    },
    {
      field: 'deviceId',
      headerName: t('devices.deviceId'),
      flex: 0.8,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => (
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            height: '100%',
            width: '100%',
            '& .copy-button': { opacity: 0, transition: 'opacity 0.2s ease' },
            '&:hover .copy-button': { opacity: 1 },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value ?? t('common.notAvailable')}
          </Typography>
          {params.value != null && (
            <IconButton
              className="copy-button"
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                handleCopyId(params.row.raw);
              }}
              sx={{
                color: 'text.secondary',
                '&:hover': { color: 'primary.main' },
              }}
            >
              <ContentCopyRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      ),
    },
    {
      field: 'deviceType',
      headerName: t('devices.deviceType'),
      flex: 1,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => {
        const label = formatDeviceTypeLabel(params.value);

        return label ? (
          <Chip label={label} size="small" variant="outlined" />
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('common.notAvailable')}
          </Typography>
        );
      },
    },
    {
      field: 'description',
      headerName: t('devices.description'),
      flex: 1.2,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Typography
            variant="body2"
            color={params.value ? 'text.primary' : 'text.secondary'}
          >
            {params.value || t('common.notAvailable')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'location',
      headerName: t('devices.location'),
      flex: 1.1,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <Typography
            variant="body2"
            color={params.value ? 'text.primary' : 'text.secondary'}
          >
            {params.value || t('devices.noLocation')}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'mappedUnits',
      headerName: t('sensors.meteredUnits'),
      flex: 1.8,
      sortable: false,
      valueGetter: (_value, row) => {
        const uniqueUnits = Array.from(
          new Map(
            (row.mappedUnits ?? [])
              .filter((unit) => unit.unitId)
              .map((unit) => [unit.unitId, unit.unitName]),
          ).values(),
        );

        return uniqueUnits.filter(Boolean).join(', ');
      },
      renderCell: (params: GridRenderCellParams<DeviceRow>) => {
        const mappedUnits = params.row.mappedUnits || [];

        if (mappedUnits.length === 0) {
          return (
            <Box display="flex" alignItems="center" height="100%">
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            </Box>
          );
        }

        const uniqueMappedUnits = Array.from(
          new Map(
            mappedUnits
              .filter((unit: GetDeviceUnitDto) => unit.unitId)
              .map((unit: GetDeviceUnitDto) => [unit.unitId, unit]),
          ).values(),
        );

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'flex-start',
                alignContent: 'flex-start',
                width: '100%',
                py: 0.5,
              }}
            >
              {uniqueMappedUnits.map((unit: GetDeviceUnitDto) => (
                <Chip
                  key={unit.unitId}
                  component={Link}
                  to={`/$accountId/units/${unit.unitId}`}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => event.stopPropagation()}
                  icon={<LightRoundedIcon sx={{ color: 'white !important' }} />}
                  label={`${unit.unitName || t('common.notAvailable')} (${unit.unitId})`}
                  color="info"
                  size="small"
                  clickable
                  sx={{
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    color: 'white',
                    '& .MuiChip-label': {
                      color: 'white',
                    },
                    '&:hover': {
                      opacity: 0.9,
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'tags',
      headerName: t('devices.tags'),
      flex: 1.3,
      sortable: false,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            width: '100%',
            height: '100%',
            alignItems: 'center',
            alignContent: 'center',
          }}
        >
          {params.value?.length > 0 ? (
            params.value.map((tag: DeviceTag, index: number) => {
              const label = formatDeviceTag(tag);

              return (
                <DeviceTagChip
                  key={`${tag?.key ?? 'tag'}-${index}`}
                  label={label || t('common.notAvailable')}
                />
              );
            })
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t('common.notAvailable')}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'capabilities',
      headerName: t('devices.capabilities'),
      flex: 1.6,
      sortable: false,
      renderCell: (params: GridRenderCellParams<DeviceRow>) => {
        const capabilityNames = Object.keys(params.value ?? {});
        const visibleCapabilities = capabilityNames.slice(0, 3);
        const remainingCount =
          capabilityNames.length - visibleCapabilities.length;

        return (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
              width: '100%',
              height: '100%',
              alignItems: 'center',
              alignContent: 'center',
            }}
          >
            {capabilityNames.length > 0 ? (
              <>
                {visibleCapabilities.map((capabilityName) => (
                  <Chip
                    key={capabilityName}
                    size="small"
                    label={formatCapabilityLabel(capabilityName)}
                    variant="outlined"
                  />
                ))}
                {remainingCount > 0 && (
                  <Chip
                    size="small"
                    label={`+${remainingCount}`}
                    variant="outlined"
                  />
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('common.notAvailable')}
              </Typography>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label={t('devices.allTypes')}
            variant={selectedType === 'all' ? 'filled' : 'outlined'}
            color={selectedType === 'all' ? 'primary' : 'default'}
            onClick={() => setSelectedType('all')}
          />
          {availableTypes.map((deviceType) => (
            <Chip
              key={deviceType}
              label={formatDeviceTypeLabel(deviceType)}
              variant={selectedType === deviceType ? 'filled' : 'outlined'}
              color={selectedType === deviceType ? 'primary' : 'default'}
              onClick={() => setSelectedType(deviceType)}
            />
          ))}
        </Box>
      </Stack>

      <Box data-tour="device-overview-grid">
        <DataGridPremium
          showToolbar
          rows={filteredRows}
          columns={columns}
          localeText={localeText}
          loading={isLoading}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
          }}
          slots={{
            toolbar: DeviceGridToolbar,
          }}
          onRowClick={(params) => handleRowClick(params.row as DeviceRow)}
          disableRowSelectionOnClick
          sx={{
            minHeight: 480,
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-cell[data-field="tags"]': {
              alignItems: 'flex-start',
            },
            '& .MuiDataGrid-cell[data-field="mappedUnits"]': {
              alignItems: 'flex-start',
            },
          }}
        />
      </Box>
    </Stack>
  );
};

export default DeviceDataGrid;
